<template>
  <div class="deal-record-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>成交台账</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出台账</el-button>
            <el-button type="primary" @click="handleCreate">新增台账</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :model="queryParams" class="search-form" label-width="80px">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="客户">
              <el-input
                v-model="queryParams.customerName"
                placeholder="请输入客户姓名"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="审批状态">
              <el-select
                v-model="queryParams.status"
                placeholder="全部"
                clearable
                style="width: 100%"
              >
                <el-option label="全部" value="" />
                <el-option label="草稿" value="draft" />
                <el-option label="待审批" value="pending" />
                <el-option label="审批中" value="approving" />
                <el-option label="已通过" value="approved" />
                <el-option label="已驳回" value="rejected" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="成交日期">
              <el-date-picker
                v-model="dealDateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label=" ">
              <div class="action-buttons-inline">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 台账列表 -->
      <div class="table-wrapper">
        <el-table v-loading="loading" :data="dealRecordList" border stripe style="width: 100%" table-layout="auto">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="customerName" label="客户姓名" width="150" show-overflow-tooltip />
        <el-table-column prop="productName" label="产品名称" width="150" show-overflow-tooltip />
        <el-table-column prop="dealAmount" label="成交金额" width="130" align="right">
          <template #default="{ row }">
            <span style="color: #67c23a; font-weight: bold">¥{{ formatMoney(row.dealAmount || 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="dealDate" label="成交日期" width="120" />
        <el-table-column prop="dealType" label="成交类型" width="120">
          <template #default="{ row }">
            {{ getDealTypeName(row.dealType) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="审批状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="paymentStatus" label="付款状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getPaymentStatusType(row.paymentStatus)" size="small">
              {{ getPaymentStatusName(row.paymentStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="关联合同" width="150" show-overflow-tooltip>
          <template #default="{ row }">
            <span v-if="row.contractNumber" class="contract-link" @click="handleViewContract(row)">
              {{ row.contractNumber }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)" v-if="row.status === 'draft'">编辑</el-button>
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button
                type="success"
                size="small"
                @click="handleCorrect(row)"
                v-if="row.status === 'rejected' || row.status === 'REJECTED'"
              >
                修正
              </el-button>
            </el-tooltip>
            <el-button type="danger" size="small" @click="handleWithdrawDealRecordFromList(row)" v-if="row.status === 'pending'">撤销</el-button>
            <el-button type="info" size="small" @click="handleViewApprovalProgress(row)" v-if="row.status !== 'draft'">审批进度</el-button>
          </template>
        </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑成交台账对话框 -->
    <el-dialog
      v-model="dealRecordFormVisible"
      :title="dealRecordFormMode === 'add' ? '新增成交台账' : (dealRecordFormMode === 'correct' ? '修正成交台账' : '编辑成交台账')"
      width="900px"
      :close-on-click-modal="false"
      @close="handleDealRecordFormClose"
    >
      <el-form
        ref="dealRecordFormRef"
        :model="dealRecordFormData"
        :rules="dealRecordFormRules"
        label-width="140px"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="客户" prop="customerId">
              <div class="customer-selector">
                <el-input
                  v-model="dealRecordFormData.customerName"
                  placeholder="请选择客户"
                  readonly
                  style="width: 100%"
                  @click="openDealRecordCustomerPicker"
                >
                  <template #suffix>
                    <el-icon v-if="dealRecordFormData.customerId" class="clear-icon" @click.stop="clearDealRecordCustomer">
                      <Close />
                    </el-icon>
                    <el-icon class="arrow-icon">
                      <ArrowDown />
                    </el-icon>
                  </template>
                </el-input>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="关联合同编号" prop="contractNumber">
              <div class="contract-selector">
                <el-input
                  v-model="dealRecordFormData.contractNumber"
                  placeholder="请选择合同编号(已通过的)"
                  readonly
                  style="width: calc(100% - 80px); margin-right: 8px"
                />
                <el-button
                  type="primary"
                  @click="openDealRecordContractPicker"
                >
                  选择
                </el-button>
              </div>
              <div v-if="dealRecordFormData.contractAmount" class="contract-amount-hint">
                合同金额：¥{{ formatMoney(dealRecordFormData.contractAmount) }}
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="合同金额">
              <el-input
                v-model="dealRecordFormData.contractAmountDisplay"
                placeholder="选择合同后自动带出"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="成交金额(元)" prop="dealAmount">
              <el-input-number
                v-model="dealRecordFormData.dealAmount"
                :min="0"
                :precision="2"
                :step="100"
                placeholder="请输入成交金额"
                style="width: 100%"
              />
              <div class="form-hint">成交金额不能大于合同金额</div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="成交类型" prop="dealType">
              <el-select v-model="dealRecordFormData.dealType" placeholder="请选择成交类型" style="width: 100%">
                <el-option
                  v-for="item in dealTypeOptions"
                  :key="item.dataCode"
                  :label="item.dataName"
                  :value="item.dataCode"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="成交日期" prop="dealDate">
              <el-date-picker
                v-model="dealRecordFormData.dealDate"
                type="date"
                placeholder="请选择成交日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="佣金比例(%)">
              <el-input-number
                v-model="dealRecordFormData.commissionRate"
                :min="0"
                :max="100"
                :precision="2"
                placeholder="请输入佣金比例(0-100)"
                style="width: 100%"
              />
              <div v-if="dealRecordFormData.commissionAmount" class="form-hint">
                佣金金额：¥{{ formatMoney(dealRecordFormData.commissionAmount) }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="付款状态">
              <el-select v-model="dealRecordFormData.paymentStatus" placeholder="请选择付款状态" style="width: 100%">
                <el-option
                  v-for="item in paymentStatusOptions"
                  :key="item.dataCode"
                  :label="item.dataName"
                  :value="item.dataCode"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20" v-if="dealRecordFormData.paymentStatus === 'partial' || dealRecordFormData.paymentStatus === 'paid'">
          <el-col :span="12" v-if="dealRecordFormData.paymentStatus === 'partial' || dealRecordFormData.paymentStatus === 'partially_paid'">
            <el-form-item label="付款金额(元)" prop="paymentAmount">
              <el-input-number
                v-model="dealRecordFormData.paymentAmount"
                :min="0"
                :precision="2"
                placeholder="请输入付款金额"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="付款日期">
              <el-date-picker
                v-model="dealRecordFormData.paymentDate"
                type="date"
                placeholder="请选择付款日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="发票编号">
              <el-input
                v-model="dealRecordFormData.invoiceNumber"
                placeholder="请输入发票编号（可选）"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="产品名称">
              <el-input
                v-model="dealRecordFormData.productName"
                placeholder="请输入产品名称"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="产品类别">
              <el-input
                v-model="dealRecordFormData.productCategory"
                placeholder="请输入产品类别"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input
                v-model="dealRecordFormData.remark"
                type="textarea"
                :rows="4"
                placeholder="请输入备注信息"
                maxlength="500"
                show-word-limit
              />
            </el-form-item>
          </el-col>
        </el-row>

        <template v-if="enterpriseCrmEnabled && (dealRecordFormMode === 'add' || dealRecordFormMode === 'correct')">
          <el-row :gutter="20">
            <el-col :span="24">
              <el-divider content-position="left">回访计划</el-divider>
              <el-alert
                class="visit-plan-alert"
                type="info"
                :closable="false"
                show-icon
                title="保存台账时即写入待办；成交日满30天/满90天当日，会在工作台「今日任务」中显示。"
              />
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="成交满30天回访">
                <el-switch v-model="dealRecordFormData.visitPlan30" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="成交满90天回访">
                <el-switch v-model="dealRecordFormData.visitPlan90" />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="附件">
              <AttachmentUpload 
                ref="dealRecordAttachmentUploadRef"
                v-model="dealRecordFormData.attachments"
                :max-count="10"
                :business-type="'DEAL'"
                :module="'成交台账'"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dealRecordFormVisible = false">取消</el-button>
          <el-button type="info" :loading="dealRecordSaving" @click="handleSaveDealRecordDraft">保存草稿</el-button>
          <div style="position: relative; display: inline-block;">
            <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
              个人用户点提交审批直接通过
            </div>
            <el-button class="submit-approval-btn" :loading="dealRecordSubmitting" @click="handleSubmitDealRecord">提交审批</el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 客户选择对话框 -->
    <el-dialog
      v-model="dealRecordCustomerPickerVisible"
      title="选择客户"
      width="600px"
      :close-on-click-modal="false"
      class="customer-picker-dialog"
    >
      <div class="customer-picker-content">
        <div class="customer-picker-search">
          <el-input
            v-model="dealRecordCustomerPickerKeyword"
            placeholder="客户姓名/电话"
            clearable
            @keyup.enter="handleDealRecordCustomerSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" @click="handleDealRecordCustomerSearch">搜索</el-button>
        </div>
        <div class="customer-picker-list" v-loading="dealRecordCustomerPickerLoading">
          <div
            v-for="item in dealRecordCustomerPickerList"
            :key="item.id"
            class="customer-picker-item"
            :class="{ active: dealRecordFormData.customerId === item.id }"
            @click="selectDealRecordCustomer(item)"
          >
            <div class="customer-item-main">
              <div class="customer-name">{{ item.customerName }}</div>
              <div class="customer-meta">
                <span v-if="item.contactName">{{ item.contactName }}</span>
                <span v-else class="no-contact">暂无联系人</span>
                <span v-if="item.contactPhone" class="phone">{{ item.contactPhone }}</span>
              </div>
            </div>
            <el-icon v-if="dealRecordFormData.customerId === item.id" class="check-icon">
              <Check />
            </el-icon>
          </div>
          <el-empty
            v-if="!dealRecordCustomerPickerLoading && dealRecordCustomerPickerList.length === 0"
            description="暂无客户数据"
            :image-size="100"
          />
        </div>
        <div class="customer-picker-pagination" v-if="dealRecordCustomerPickerTotal > 0">
          <el-pagination
            v-model:current-page="dealRecordCustomerPickerPage"
            v-model:page-size="dealRecordCustomerPickerPageSize"
            :total="dealRecordCustomerPickerTotal"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleDealRecordCustomerPageSizeChange"
            @current-change="handleDealRecordCustomerPageChange"
          />
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dealRecordCustomerPickerVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmDealRecordCustomerSelection" :disabled="!dealRecordFormData.customerId">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 合同选择对话框 -->
    <el-dialog
      v-model="dealRecordContractPickerVisible"
      title="选择合同"
      width="800px"
      :close-on-click-modal="false"
    >
      <div v-loading="dealRecordContractPickerLoading">
        <el-table
          :data="dealRecordContractPickerList"
          border
          stripe
          @row-click="selectDealRecordContract"
        >
          <el-table-column prop="contractNumber" label="合同编号" width="180" />
          <el-table-column prop="contractType" label="合同类型" width="120">
            <template #default="{ row }">
              {{ getContractTypeName(row.contractType) }}
            </template>
          </el-table-column>
          <el-table-column prop="contractAmount" label="合同金额" width="130" align="right">
            <template #default="{ row }">
              <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(row.contractAmount || 0) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="startDate" label="开始日期" width="120" />
          <el-table-column prop="endDate" label="结束日期" width="120" />
          <el-table-column prop="contractStatus" label="状态" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="getContractStatusType(row.contractStatus)" size="small">
                {{ getContractStatusName(row.contractStatus) }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination" style="margin-top: 16px" v-if="dealRecordContractPickerTotal > 0">
          <el-pagination
            v-model:current-page="dealRecordContractPickerPage"
            v-model:page-size="dealRecordContractPickerPageSize"
            :total="dealRecordContractPickerTotal"
            :page-sizes="[10, 20, 50]"
            layout="total, sizes, prev, pager, next"
            @size-change="handleDealRecordContractPageSizeChange"
            @current-change="handleDealRecordContractPageChange"
          />
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dealRecordContractPickerVisible = false">取消</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 成交台账详情弹窗 -->
    <el-dialog
      v-model="dealRecordDetailDialogVisible"
      title="成交台账详情"
      width="1400px"
      @close="handleDealRecordDetailDialogClose"
    >
      <div v-loading="dealRecordDetailLoading" class="contract-detail-container">
        <!-- 左侧：成交台账详情 -->
        <div class="contract-detail-left" :class="{ 'full-width': dealRecordDetail.status === 'draft' }">
          <div class="contract-detail">
            <!-- 基础信息卡片 -->
            <el-card class="detail-card" shadow="never">
              <template #header>
                <div class="card-title">
                  <span class="title-text">基础信息</span>
                  <span class="title-tip">成交台账基础资料</span>
                </div>
              </template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="ID">
                  {{ dealRecordDetail.id || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="客户姓名">
                  {{ dealRecordDetail.customerName || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="成交类型">
                  <el-tag size="small">
                    {{ getDealTypeName(dealRecordDetail.dealType) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="审批状态">
                  <el-tag :type="getStatusType(dealRecordDetail.status)" size="small">
                    {{ getStatusName(dealRecordDetail.status) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="成交金额">
                  <span style="color: #67c23a; font-weight: bold">¥{{ formatMoney(dealRecordDetail.dealAmount || 0) }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="成交日期">
                  {{ dealRecordDetail.dealDate || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="佣金比例">
                  {{ dealRecordDetail.commissionRate ? `${dealRecordDetail.commissionRate}%` : '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="佣金金额">
                  <span style="color: #67c23a; font-weight: bold">¥{{ formatMoney(dealRecordDetail.commissionAmount || 0) }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="关联合同编号">
                  <span v-if="dealRecordDetail.contractNumber" class="contract-link" @click="handleViewContractFromDetail">
                    {{ dealRecordDetail.contractNumber }}
                  </span>
                  <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="合同金额">
                  <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(dealRecordDetail.contractAmount || 0) }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="产品名称">
                  {{ dealRecordDetail.productName || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="产品类别">
                  {{ dealRecordDetail.productCategory || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="付款状态">
                  <el-tag :type="getPaymentStatusType(dealRecordDetail.paymentStatus)" size="small">
                    {{ getPaymentStatusName(dealRecordDetail.paymentStatus) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item
                  label="付款金额"
                  v-if="dealRecordDetail.paymentStatus === 'partial' || dealRecordDetail.paymentStatus === 'partially_paid'"
                >
                  <span style="color: #67c23a; font-weight: bold">¥{{ formatMoney(dealRecordDetail.paymentAmount || 0) }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="付款日期">
                  {{ dealRecordDetail.paymentDate || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="发票编号">
                  {{ dealRecordDetail.invoiceNumber || '-' }}
                </el-descriptions-item>
              </el-descriptions>
            </el-card>

            <!-- 备注卡片 -->
            <el-card class="detail-card" shadow="never" v-if="dealRecordDetail.remark">
              <template #header>
                <div class="card-title">
                  <span class="title-text">备注</span>
                </div>
              </template>
              <el-descriptions :column="1" border>
                <el-descriptions-item label="备注">
                  <div style="white-space: pre-wrap; word-break: break-word;">
                    {{ dealRecordDetail.remark || '-' }}
                  </div>
                </el-descriptions-item>
              </el-descriptions>
            </el-card>

            <!-- 附件卡片 -->
            <el-card class="detail-card" shadow="never" v-if="dealRecordDetail.attachments && dealRecordDetail.attachments.length > 0">
              <template #header>
                <div class="card-title">
                  <span class="title-text">附件</span>
                </div>
              </template>
              <div class="attachment-list">
                <div
                  v-for="(attachment, index) in dealRecordDetail.attachments"
                  :key="index"
                  :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
                >
                  <template v-if="isAttachmentImage(attachment)">
                    <div class="attachment-image-wrapper">
                      <el-image
                        :src="getAttachmentUrl(attachment)"
                        :alt="getAttachmentFileName(attachment)"
                        :preview-src-list="getImagePreviewList()"
                        :initial-index="getImageIndex(index)"
                        fit="cover"
                        class="attachment-image-preview"
                        :preview-teleported="true"
                        crossorigin="anonymous"
                        referrerpolicy="no-referrer-when-downgrade"
                        @error="handleImageError"
                      >
                        <template #error>
                          <div class="image-slot">
                            <el-icon><Picture /></el-icon>
                          </div>
                        </template>
                      </el-image>
                      <!-- 下载按钮覆盖层 -->
                      <div class="download-overlay">
                        <el-button
                          type="primary"
                          :icon="Download"
                          circle
                          size="small"
                          class="download-overlay-btn"
                          @click.stop="handleDownloadAttachment(attachment)"
                          title="下载图片"
                        />
                      </div>
                    </div>
                    <div class="attachment-image-info">
                      <div class="attachment-image-details">
                        <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                          {{ getAttachmentFileName(attachment) }}
                        </span>
                        <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                          ({{ formatFileSize(getAttachmentSize(attachment)) }})
                        </span>
                      </div>
                    </div>
                  </template>
                  
                  <!-- 非图片附件：显示图标 -->
                  <template v-else>
                    <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                      <el-icon class="attachment-icon">
                        <component :is="getFileIcon(attachment)" />
                      </el-icon>
                      <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                        {{ getFileTypeLabel(attachment) }}
                      </span>
                    </div>
                    <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                      {{ getAttachmentFileName(attachment) }}
                    </span>
                    <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                      ({{ formatFileSize(getAttachmentSize(attachment)) }})
                    </span>
                    <div class="attachment-actions">
                      <el-button
                        type="primary"
                        size="small"
                        :icon="Download"
                        link
                        @click="handleDownloadAttachment(attachment)"
                      >
                        下载
                      </el-button>
                    </div>
                  </template>
                </div>
              </div>
            </el-card>

            <!-- 审批信息卡片（草稿状态下不显示） -->
            <el-card class="detail-card" shadow="never" v-if="dealRecordDetail.status !== 'draft'">
              <template #header>
                <div class="card-title">
                  <span class="title-text">审批信息</span>
                </div>
              </template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="创建人">
                  {{ dealRecordDetail.createBy || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="创建时间">
                  {{ formatDateTime(dealRecordDetail.createTime) }}
                </el-descriptions-item>
              </el-descriptions>
            </el-card>
          </div>
        </div>
        
        <!-- 右侧：审批记录 -->
        <div class="contract-detail-right" v-if="dealRecordDetail.status !== 'draft'">
          <el-card class="approval-record-card" shadow="never">
            <template #header>
              <div class="card-title">
                <span class="title-text">审批记录</span>
              </div>
            </template>
            <div v-loading="dealRecordApprovalRecordLoading">
              <ApprovalTimeline :records="dealRecordApprovalRecords" />
            </div>
          </el-card>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <div class="footer-right">
            <el-button @click="handleEditFromDetail" v-if="dealRecordDetail.status === 'draft'">编辑</el-button>
            <div v-if="dealRecordDetail.status === 'draft'" style="position: relative; display: inline-block;">
              <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
                个人用户点提交审批直接通过
              </div>
              <el-button class="submit-approval-btn" @click="handleSubmitDealRecordApproval">提交审批</el-button>
            </div>
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button
                type="success"
                @click="handleCorrectFromDetail"
                v-if="dealRecordDetail.status === 'rejected' || dealRecordDetail.status === 'REJECTED'"
              >
                修正
              </el-button>
            </el-tooltip>
            <el-button type="danger" @click="handleWithdrawDealRecord" v-if="dealRecordDetail.status === 'pending'">撤销审批</el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 审批记录对话框 -->
    <el-dialog
      v-model="approvalProgressDialogVisible"
      title="审批进度"
      width="800px"
      @close="handleApprovalProgressDialogClose"
    >
      <div v-loading="approvalProgressLoading">
        <ApprovalTimeline :records="approvalProgressRecords" />
      </div>
      <template #footer>
        <div class="dialog-footer">
          <div class="footer-right">
            <el-button @click="approvalProgressDialogVisible = false">关闭</el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 合同详情弹窗 -->
    <el-dialog
      v-model="contractDetailDialogVisible"
      title="合同详情"
      width="1400px"
      @close="handleContractDetailDialogClose"
    >
      <div v-loading="contractDetailLoading" class="contract-detail-container">
        <!-- 合同详情 -->
        <div class="contract-detail-left full-width">
          <div class="contract-detail">
            <!-- 基础信息卡片 -->
            <el-card class="detail-card" shadow="never">
              <template #header>
                <div class="card-title">
                  <span class="title-text">基础信息</span>
                  <span class="title-tip">合同基础资料</span>
                </div>
              </template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="合同编号">
                  {{ contractDetail.contractNumber || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="客户姓名">
                  {{ contractDetail.customerName || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="合同类型">
                  <el-tag :type="contractDetail.contractType === 'deal' ? 'success' : 'warning'" size="small">
                    {{ getContractTypeName(contractDetail.contractType) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="合同状态">
                  <el-tag :type="getContractStatusType(contractDetail.contractStatus)" size="small">
                    {{ getContractStatusName(contractDetail.contractStatus) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="合同金额">
                  <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(contractDetail.contractAmount || 0) }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="开始日期">
                  {{ contractDetail.startDate || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="结束日期">
                  {{ contractDetail.endDate || '-' }}
                </el-descriptions-item>
              </el-descriptions>
            </el-card>

            <!-- 备注卡片 -->
            <el-card class="detail-card" shadow="never">
              <template #header>
                <div class="card-title">
                  <span class="title-text">备注</span>
                </div>
              </template>
              <el-descriptions :column="1" border>
                <el-descriptions-item label="备注">
                  <div style="white-space: pre-wrap; word-break: break-word;">
                    {{ contractDetail.remark || '-' }}
                  </div>
                </el-descriptions-item>
              </el-descriptions>
            </el-card>

            <!-- 合同图片卡片 -->
            <el-card class="detail-card" shadow="never" v-if="contractDetail.contractImages && contractDetail.contractImages.length > 0">
              <template #header>
                <div class="card-title">
                  <span class="title-text">合同图片</span>
                </div>
              </template>
              <ImagePreview :model-value="contractDetail.contractImages" />
            </el-card>

            <!-- 合同附件卡片 -->
            <el-card class="detail-card" shadow="never" v-if="contractDetail.contractAttachments && contractDetail.contractAttachments.length > 0">
              <template #header>
                <div class="card-title">
                  <span class="title-text">合同附件</span>
                </div>
              </template>
              <AttachmentUpload 
                :model-value="contractDetail.contractAttachments"
                :show-actions="false"
                :show-download="true"
                :show-tip="false"
                :show-add-button="false"
              />
            </el-card>

            <!-- 审批信息卡片 -->
            <el-card class="detail-card" shadow="never">
              <template #header>
                <div class="card-title">
                  <span class="title-text">审批信息</span>
                </div>
              </template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="起草人">
                  {{ contractDetail.draftBy || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="起草时间">
                  {{ formatDateTime(contractDetail.draftTime) }}
                </el-descriptions-item>
                <el-descriptions-item label="审核人">
                  {{ contractDetail.reviewBy || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="审核时间">
                  {{ formatDateTime(contractDetail.reviewTime) }}
                </el-descriptions-item>
                <el-descriptions-item label="创建人">
                  {{ contractDetail.createBy || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="创建时间">
                  {{ formatDateTime(contractDetail.createTime) }}
                </el-descriptions-item>
              </el-descriptions>
            </el-card>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <div class="footer-right">
            <el-button @click="contractDetailDialogVisible = false">关闭</el-button>
          </div>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Close, ArrowDown, Search, Check, Download, Picture, Document, VideoPlay } from '@element-plus/icons-vue'
import { formatFileSize, isImageFile, downloadFileFromServer } from '../utils/fileUtils'
import { getFileName, getPreviewUrl } from '../api/file'
import AttachmentUpload from '../components/AttachmentUpload.vue'
import ImagePreview from '../components/ImagePreview.vue'
import ApprovalTimeline from '../components/ApprovalTimeline.vue'
import {
  getDealRecordList,
  getDealRecordDetail,
  getContractDetail,
  getApprovalRecords,
  getContractList,
  getCustomerList,
  getCustomerCrmMeta,
  createDealRecord,
  updateDealRecord,
  withdrawDealRecord,
  checkNeedApproval,
  exportDealRecordList
} from '../api/salesman'
import { checkQuotaWithLocalFallback, QUOTA_TYPE } from '../utils/quotaCheck'

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

const crmMeta = ref({
  enterpriseCrmEnabled: false,
  opportunityStageLoseCode: '',
  opportunityStageWinCode: ''
})

const enterpriseCrmEnabled = computed(() => !!crmMeta.value?.enterpriseCrmEnabled)

const loadDealCrmMeta = async () => {
  if (!getCompanyId()) return
  try {
    const res = await getCustomerCrmMeta()
    if (res.code === '000000' && res.data) {
      crmMeta.value = { ...crmMeta.value, ...res.data }
    }
  } catch (e) {
    console.warn('加载 CRM 元数据失败', e)
  }
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

const loading = ref(false)
const dealRecordList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const queryParams = reactive({
  customerId: null,
  customerName: '',
  status: '',
  startDate: '',
  endDate: '',
  page: 1,
  pageSize: 10
})

// 成交日期范围（用于日期选择器）
const dealDateRange = ref([])

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getDealTypeName = (type) => {
  const map = {
    new_customer: '新客户',
    renewal: '续约',
    upsell: '增购',
    cross_sell: '交叉销售'
  }
  return map[type] || type || '-'
}

const getStatusName = (status) => {
  const map = {
    draft: '草稿',
    pending: '待审批',
    approving: '审批中',
    approved: '已通过',
    rejected: '已驳回'
  }
  return map[status] || status || '-'
}

const getStatusType = (status) => {
  const map = {
    draft: 'info',
    pending: 'warning',
    approving: 'warning',
    approved: 'success',
    rejected: 'danger',
    cancelled: 'info'
  }
  return map[status] || 'info'
}

const getPaymentStatusName = (status) => {
  const map = {
    unpaid: '未付款',
    partial: '部分付款',
    paid: '已付款'
  }
  return map[status] || status || '-'
}

const getPaymentStatusType = (status) => {
  const map = {
    unpaid: 'danger',
    partial: 'warning',
    paid: 'success'
  }
  return map[status] || 'info'
}

const loadDealRecordList = async () => {
  loading.value = true
  try {
    const res = await getDealRecordList(queryParams)
    if (res.code === '000000') {
      dealRecordList.value = res.data?.records || res.data?.list || []
      total.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取成交台账列表失败:', error)
    ElMessage.error('获取成交台账列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 处理成交日期范围
  if (dealDateRange.value && dealDateRange.value.length === 2) {
    queryParams.startDate = dealDateRange.value[0]
    queryParams.endDate = dealDateRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  
  queryParams.page = 1
  loadDealRecordList()
}

const handleReset = () => {
  queryParams.customerId = null
  queryParams.customerName = ''
  queryParams.status = ''
  queryParams.startDate = ''
  queryParams.endDate = ''
  queryParams.page = 1
  dealDateRange.value = []
  loadDealRecordList()
}

// 新增/编辑成交台账对话框相关
const dealRecordFormVisible = ref(false)
const dealRecordAttachmentUploadRef = ref(null)
const dealRecordFormMode = ref('add') // 'add' 或 'edit'
const dealRecordFormRef = ref(null)
const dealRecordSaving = ref(false)
const dealRecordSubmitting = ref(false)
const dealRecordFormData = reactive({
  id: null,
  customerId: null,
  customerName: '',
  contractId: null,
  contractNumber: '',
  contractAmount: null,
  contractAmountDisplay: '',
  dealAmount: null,
  dealType: '',
  dealDate: '',
  commissionRate: 0,
  commissionAmount: 0,
  paymentStatus: 'unpaid',
  paymentAmount: null,
  paymentDate: '',
  invoiceNumber: '',
  productName: '',
  productCategory: '',
  remark: '',
  attachments: [],
  visitPlan30: false,
  visitPlan90: false
})

const appendVisitPlanToCreatePayload = (payload) => {
  if (enterpriseCrmEnabled.value && (dealRecordFormMode.value === 'add' || dealRecordFormMode.value === 'correct')) {
    payload.visitPlan30 = !!dealRecordFormData.visitPlan30
    payload.visitPlan90 = !!dealRecordFormData.visitPlan90
  }
}

const dealRecordFormRules = {
  customerId: [{ required: true, message: '请选择客户', trigger: 'change' }],
  dealAmount: [
    { required: true, message: '请输入成交金额', trigger: 'blur' },
    { type: 'number', min: 0, message: '成交金额必须大于0', trigger: 'blur' }
  ],
  dealType: [{ required: true, message: '请选择成交类型', trigger: 'change' }],
  dealDate: [{ required: true, message: '请选择成交日期', trigger: 'change' }],
  paymentAmount: [
    {
      validator: (_rule, value, callback) => {
        if (dealRecordFormData.paymentStatus !== 'partial' && dealRecordFormData.paymentStatus !== 'partially_paid') {
          callback()
          return
        }
        if (value === null || value === undefined || value === '') {
          callback(new Error('部分付款时，付款金额不能为空'))
          return
        }
        const num = Number(value)
        if (isNaN(num) || num <= 0) {
          callback(new Error('付款金额必须大于0'))
          return
        }
        if (dealRecordFormData.dealAmount && num >= Number(dealRecordFormData.dealAmount)) {
          callback(new Error('付款金额必须小于成交金额'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ]
}

// 成交类型选项（参考小程序）
const dealTypeOptions = ref([
  { dataCode: 'new_customer', dataName: '新客户' },
  { dataCode: 'renewal', dataName: '续约' },
  { dataCode: 'upsell', dataName: '增购' },
  { dataCode: 'cross_sell', dataName: '交叉销售' }
])

// 付款状态选项
const paymentStatusOptions = ref([
  { dataCode: 'unpaid', dataName: '未付款' },
  { dataCode: 'partial', dataName: '部分付款' },
  { dataCode: 'paid', dataName: '已付款' }
])

// 客户选择对话框相关
const dealRecordCustomerPickerVisible = ref(false)
const dealRecordCustomerPickerKeyword = ref('')
const dealRecordCustomerPickerList = ref([])
const dealRecordCustomerPickerLoading = ref(false)
const dealRecordCustomerPickerPage = ref(1)
const dealRecordCustomerPickerPageSize = ref(10)
const dealRecordCustomerPickerTotal = ref(0)

// 合同选择对话框相关
const dealRecordContractPickerVisible = ref(false)
const dealRecordContractPickerList = ref([])
const dealRecordContractPickerLoading = ref(false)
const dealRecordContractPickerPage = ref(1)
const dealRecordContractPickerPageSize = ref(10)
const dealRecordContractPickerTotal = ref(0)

// 计算佣金金额
const calculateCommissionAmount = () => {
  if (dealRecordFormData.dealAmount && dealRecordFormData.commissionRate) {
    dealRecordFormData.commissionAmount = Number(
      (dealRecordFormData.dealAmount * dealRecordFormData.commissionRate / 100).toFixed(2)
    )
  } else {
    dealRecordFormData.commissionAmount = 0
  }
}

// 监听成交金额和佣金比例变化
watch(() => [dealRecordFormData.dealAmount, dealRecordFormData.commissionRate], () => {
  calculateCommissionAmount()
}, { deep: true })

// 监听合同金额变化，更新显示
watch(() => dealRecordFormData.contractAmount, (val) => {
  if (val) {
    dealRecordFormData.contractAmountDisplay = formatMoney(val)
  } else {
    dealRecordFormData.contractAmountDisplay = ''
  }
})

// 提交状态
const submitting = ref(false)

// 判断附件是否为图片
const isAttachmentImage = (attachment) => {
  if (!attachment) return false
  const fileName = getAttachmentFileName(attachment)
  return isImageFile(fileName)
}

// 获取附件文件名（优先显示原始文件名）
const getAttachmentFileName = (attachment) => {
  if (!attachment) return '未知文件'
  // 如果是对象，优先使用原始文件名
  if (typeof attachment === 'object') {
    if (attachment.originalFileName) {
      return attachment.originalFileName
    }
    if (attachment.fileName) {
      return attachment.fileName
    }
    if (attachment.name) {
      return attachment.name
    }
    if (attachment.url) {
      const parts = attachment.url.split('/')
      const fileName = parts[parts.length - 1].split('?')[0]
      return fileName || '附件'
    }
  }
  // 如果是字符串URL
  if (typeof attachment === 'string') {
    const parts = attachment.split('/')
    const fileName = parts[parts.length - 1].split('?')[0]
    return fileName || '附件'
  }
  return '未知文件'
}

// 获取附件URL
const getAttachmentUrl = (attachment) => {
  let url = ''
  if (typeof attachment === 'string') {
    url = attachment
  } else if (attachment && attachment.url) {
    url = attachment.url
  } else {
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    return url
  }
  
  // 对于COS URL，使用代理接口（解决403和CORS问题）
  return getPreviewUrl(url)
}

// 获取附件大小
const getAttachmentSize = (attachment) => {
  if (!attachment) return 0
  if (typeof attachment === 'object' && attachment.size) {
    return attachment.size
  }
  return 0
}

// 获取所有图片的预览列表（用于 el-image 的预览功能）
const getImagePreviewList = () => {
  if (!dealRecordDetail.attachments || !dealRecordDetail.attachments.length) return []
  return dealRecordDetail.attachments
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取当前图片在预览列表中的索引
const getImageIndex = (currentIndex) => {
  if (!dealRecordDetail.attachments || !dealRecordDetail.attachments.length) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = dealRecordDetail.attachments[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 处理图片加载错误
const handleImageError = (event) => {
  if (event.target) {
    event.target.style.display = 'none'
  }
}

// 获取文件扩展名
const getFileExtension = (attachment) => {
  const fileName = getAttachmentFileName(attachment)
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

// 获取文件类型图标
const getFileIcon = (attachment) => {
  const ext = getFileExtension(attachment)
  // 图片类型
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return Picture
  }
  // 视频类型
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
    return VideoPlay
  }
  // 其他类型使用文档图标
  return Document
}

// 获取文件类型标签（显示在图标上）
const getFileTypeLabel = (attachment) => {
  const ext = getFileExtension(attachment)
  const labelMap = {
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'doc': 'DOC',
    'docx': 'DOCX',
    'pdf': 'PDF',
    'txt': 'TXT',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'zip': 'ZIP',
    'rar': 'RAR',
    '7z': '7Z'
  }
  return labelMap[ext] || ''
}

// 获取文件类型图标样式类
const getFileIconClass = (attachment) => {
  const ext = getFileExtension(attachment)
  const iconClassMap = {
    // Excel
    'xls': 'icon-excel',
    'xlsx': 'icon-excel',
    // Word
    'doc': 'icon-word',
    'docx': 'icon-word',
    // PDF
    'pdf': 'icon-pdf',
    // 文本
    'txt': 'icon-txt',
    // 图片
    'jpg': 'icon-image',
    'jpeg': 'icon-image',
    'png': 'icon-image',
    'gif': 'icon-image',
    'bmp': 'icon-image',
    'webp': 'icon-image',
    // PPT
    'ppt': 'icon-ppt',
    'pptx': 'icon-ppt',
    // 压缩包
    'zip': 'icon-zip',
    'rar': 'icon-zip',
    '7z': 'icon-zip'
  }
  return iconClassMap[ext] || 'icon-default'
}

// 下载附件
const handleDownloadAttachment = async (attachment) => {
  let url = typeof attachment === 'string' ? attachment : attachment.url
  let fileName = typeof attachment === 'string' ? undefined : (attachment.originalFileName || attachment.fileName)
  
  if (!url) {
    ElMessage.warning('附件地址无效')
    return
  }
  
  try {
    await downloadFileFromServer(url, fileName)
  } catch (error) {
    console.error('下载附件失败:', error)
    ElMessage.error(error.message || '下载附件失败')
  }
}

const handleCreate = async () => {
  // 先检查成交台账额度
  const result = await checkQuotaWithLocalFallback(QUOTA_TYPE.DEAL, {
    allowLocalOperation: false,
    operationName: '新增成交台账'
  })
  
  if (!result.allowed) {
    // 额度已超限，不允许新增
    return
  }
  
  dealRecordFormMode.value = 'add'
  // 重置表单数据
  Object.keys(dealRecordFormData).forEach(key => {
    if (key === 'paymentStatus') {
      dealRecordFormData[key] = 'unpaid'
    } else if (key === 'commissionRate') {
      dealRecordFormData[key] = 0
    } else if (key === 'attachments') {
      dealRecordFormData[key] = []
    } else if (key === 'visitPlan30' || key === 'visitPlan90') {
      dealRecordFormData[key] = false
    } else if (typeof dealRecordFormData[key] === 'number') {
      dealRecordFormData[key] = null
    } else {
      dealRecordFormData[key] = ''
    }
  })
  dealRecordFormData.id = null
  dealRecordFormVisible.value = true
  // 清除表单验证
  nextTick(() => {
    if (dealRecordFormRef.value) {
      dealRecordFormRef.value.clearValidate()
    }
  })
}

const handleEdit = async (row, correct = false) => {
  if (!correct && row.status !== 'draft') {
    ElMessage.warning('只能编辑草稿状态的成交台账')
    return
  }
  dealRecordFormMode.value = correct ? 'correct' : 'edit'
  dealRecordFormLoading.value = true
  try {
    const res = await getDealRecordDetail(row.id)
    if (res.code === '000000' && res.data) {
      // 填充表单数据
      dealRecordFormData.id = correct ? null : res.data.id // 修正模式下不设置id，创建新数据
      dealRecordFormData.customerId = res.data.customerId
      dealRecordFormData.customerName = res.data.customerName || ''
      dealRecordFormData.contractId = res.data.contractId
      dealRecordFormData.contractNumber = res.data.contractNumber || ''
      dealRecordFormData.contractAmount = res.data.contractAmount
      dealRecordFormData.dealAmount = res.data.dealAmount
      dealRecordFormData.dealType = res.data.dealType || ''
      dealRecordFormData.dealDate = res.data.dealDate || ''
      dealRecordFormData.commissionRate = res.data.commissionRate || 0
      dealRecordFormData.commissionAmount = res.data.commissionAmount || 0
      dealRecordFormData.paymentStatus = res.data.paymentStatus || 'unpaid'
      dealRecordFormData.paymentAmount = res.data.paymentAmount
      dealRecordFormData.paymentDate = res.data.paymentDate || ''
      dealRecordFormData.invoiceNumber = res.data.invoiceNumber || ''
      dealRecordFormData.productName = res.data.productName || ''
      dealRecordFormData.productCategory = res.data.productCategory || ''
      dealRecordFormData.remark = res.data.remark || ''
      dealRecordFormData.visitPlan30 = false
      dealRecordFormData.visitPlan90 = false
      
      // 处理附件字段（兼容多种字段名：attachment_urls、attachments、attachmentInfos）
      const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      let allAttachments = []
      
      // 优先使用 attachmentInfos（包含文件大小信息）
      if (res.data.attachmentInfos && Array.isArray(res.data.attachmentInfos) && res.data.attachmentInfos.length > 0) {
        allAttachments = res.data.attachmentInfos.map(att => {
          let url = att.fileUrl || att.url || att
          if (typeof url === 'string') {
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
          }
          return {
            url,
            fileName: att.fileName || att.originalFileName || url.split('/').pop() || '附件',
            originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
            size: att.size || 0
          }
        })
      } else {
        // 兼容 attachment_urls 和 attachments 字段
        const attachmentUrls = res.data.attachment_urls || res.data.attachments || []
        allAttachments = Array.isArray(attachmentUrls) ? attachmentUrls : []
        // 异步获取原始文件名和文件大小
        allAttachments = await Promise.all(allAttachments.map(async (att) => {
          if (typeof att === 'string') {
            let url = att
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = url.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(url)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(url, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url, 
              fileName: originalFileName, // 使用原始文件名
              originalFileName, // 保存原始文件名
              size: fileSize 
            }
          }
          // 如果已经是对象格式，直接使用
          return att
        }))
      }
      
      dealRecordFormData.attachments = allAttachments
      
      dealRecordFormVisible.value = true
      // 清除表单验证
      nextTick(() => {
        if (dealRecordFormRef.value) {
          dealRecordFormRef.value.clearValidate()
        }
      })
    } else {
      ElMessage.error(res.message || '获取成交台账详情失败')
    }
  } catch (error) {
    console.error('获取成交台账详情失败:', error)
    ElMessage.error('获取成交台账详情失败')
  } finally {
    dealRecordFormLoading.value = false
  }
}

const dealRecordFormLoading = ref(false)

const handleDealRecordFormClose = () => {
  // 重置表单
  Object.keys(dealRecordFormData).forEach(key => {
    if (key === 'paymentStatus') {
      dealRecordFormData[key] = 'unpaid'
    } else if (key === 'commissionRate') {
      dealRecordFormData[key] = 0
    } else if (key === 'attachments') {
      dealRecordFormData[key] = []
    } else if (key === 'visitPlan30' || key === 'visitPlan90') {
      dealRecordFormData[key] = false
    } else if (typeof dealRecordFormData[key] === 'number') {
      dealRecordFormData[key] = null
    } else {
      dealRecordFormData[key] = ''
    }
  })
  dealRecordFormData.id = null
  if (dealRecordFormRef.value) {
    dealRecordFormRef.value.clearValidate()
  }
}

// 打开客户选择对话框
const openDealRecordCustomerPicker = () => {
  dealRecordCustomerPickerVisible.value = true
  dealRecordCustomerPickerKeyword.value = ''
  dealRecordCustomerPickerPage.value = 1
  handleDealRecordCustomerSearch()
}

// 搜索客户
const handleDealRecordCustomerSearch = async () => {
  dealRecordCustomerPickerLoading.value = true
  try {
    const res = await getCustomerList({
      customerName: dealRecordCustomerPickerKeyword.value,
      page: dealRecordCustomerPickerPage.value,
      pageSize: dealRecordCustomerPickerPageSize.value,
      // 成交台账必须关联已通过审批的合同，所以只显示有已通过合同的客户
      hasApprovedContract: true
    })
    if (res.code === '000000') {
      const customerList = res.data?.records || res.data?.list || []
      dealRecordCustomerPickerList.value = customerList
      dealRecordCustomerPickerTotal.value = res.data?.total || 0
      
      // 如果是首次打开（没有搜索关键词）且列表为空，显示提示
      if (!dealRecordCustomerPickerKeyword.value && customerList.length === 0 && dealRecordCustomerPickerPage.value === 1) {
        ElMessageBox.alert('您当前暂无成交客户', '提示', {
          confirmButtonText: '知道了',
          type: 'info',
          callback: () => {
            // 关闭客户选择对话框
            dealRecordCustomerPickerVisible.value = false
          }
        })
      }
    }
  } catch (error) {
    console.error('获取客户列表失败:', error)
    ElMessage.error('获取客户列表失败')
  } finally {
    dealRecordCustomerPickerLoading.value = false
  }
}

// 选择客户
const selectDealRecordCustomer = (customer) => {
  dealRecordFormData.customerId = customer.id
  dealRecordFormData.customerName = customer.customerName
  dealRecordCustomerPickerVisible.value = false
  // 清空合同相关数据
  dealRecordFormData.contractId = null
  dealRecordFormData.contractNumber = ''
  dealRecordFormData.contractAmount = null
}

// 清空客户
const clearDealRecordCustomer = () => {
  dealRecordFormData.customerId = null
  dealRecordFormData.customerName = ''
  dealRecordFormData.contractId = null
  dealRecordFormData.contractNumber = ''
  dealRecordFormData.contractAmount = null
}

// 客户选择分页
const handleDealRecordCustomerPageChange = (page) => {
  dealRecordCustomerPickerPage.value = page
  handleDealRecordCustomerSearch()
}

const handleDealRecordCustomerPageSizeChange = (size) => {
  dealRecordCustomerPickerPageSize.value = size
  dealRecordCustomerPickerPage.value = 1
  handleDealRecordCustomerSearch()
}

// 打开合同选择对话框
const openDealRecordContractPicker = async () => {
  if (!dealRecordFormData.customerId) {
    ElMessage.warning('请先选择客户')
    return
  }
  dealRecordContractPickerVisible.value = true
  dealRecordContractPickerPage.value = 1
  await loadDealRecordContractList()
}

// 加载合同列表
const loadDealRecordContractList = async () => {
  dealRecordContractPickerLoading.value = true
  try {
    const res = await getContractList({
      customerId: dealRecordFormData.customerId,
      contractStatus: 'approved', // 只显示已审核的合同
      page: dealRecordContractPickerPage.value,
      pageSize: dealRecordContractPickerPageSize.value
    })
    if (res.code === '000000') {
      dealRecordContractPickerList.value = res.data?.records || res.data?.list || []
      dealRecordContractPickerTotal.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取合同列表失败:', error)
    ElMessage.error('获取合同列表失败')
  } finally {
    dealRecordContractPickerLoading.value = false
  }
}

// 选择合同
const selectDealRecordContract = (contract) => {
  dealRecordFormData.contractId = contract.contractId || contract.id
  dealRecordFormData.contractNumber = contract.contractNumber
  dealRecordFormData.contractAmount = contract.contractAmount
  dealRecordContractPickerVisible.value = false
}

// 合同选择分页
const handleDealRecordContractPageChange = (page) => {
  dealRecordContractPickerPage.value = page
  loadDealRecordContractList()
}

const handleDealRecordContractPageSizeChange = (size) => {
  dealRecordContractPickerPageSize.value = size
  dealRecordContractPickerPage.value = 1
  loadDealRecordContractList()
}

// 保存草稿
const handleSaveDealRecordDraft = async () => {
  if (!dealRecordFormRef.value) return
  await dealRecordFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    // 验证成交金额不能大于合同金额
    if (dealRecordFormData.contractAmount && dealRecordFormData.dealAmount > dealRecordFormData.contractAmount) {
      await ElMessageBox.alert('成交金额不能大于合同金额！', '提示', {
        confirmButtonText: '确认',
        type: 'warning'
      })
      return
    }
    
    dealRecordSaving.value = true
    submitting.value = true
    try {
      // 使用附件上传组件的统一上传逻辑（带进度条）
      if (dealRecordAttachmentUploadRef.value) {
        try {
          const uploadedAttachments = await dealRecordAttachmentUploadRef.value.uploadAll()
          dealRecordFormData.attachments = uploadedAttachments || dealRecordFormData.attachments
        } catch (error) {
          ElMessage.error(`附件上传失败: ${error.message}`)
          throw error
        }
      }
      
      // 处理附件
      const allAttachments = dealRecordFormData.attachments.map(att => typeof att === 'string' ? att : att.url || att).filter(url => url)
      
      const formData = {
        customerId: dealRecordFormData.customerId,
        dealType: dealRecordFormData.dealType,
        dealDate: dealRecordFormData.dealDate,
        dealAmount: dealRecordFormData.dealAmount,
        commissionRate: dealRecordFormData.commissionRate || 0,
        productName: dealRecordFormData.productName || undefined,
        productCategory: dealRecordFormData.productCategory || undefined,
        contractNumber: dealRecordFormData.contractNumber || undefined,
        contractId: dealRecordFormData.contractId || undefined,
        contractAmount: dealRecordFormData.contractAmount || undefined,
        paymentStatus: dealRecordFormData.paymentStatus,
        paymentAmount: dealRecordFormData.paymentAmount || undefined,
        paymentDate: dealRecordFormData.paymentDate || undefined,
        invoiceNumber: dealRecordFormData.invoiceNumber || undefined,
        remark: dealRecordFormData.remark || undefined,
        attachments: allAttachments,
        status: 'draft'
      }
      
      let res
      if (dealRecordFormMode.value === 'add') {
        appendVisitPlanToCreatePayload(formData)
        res = await createDealRecord(formData)
      } else {
        formData.id = dealRecordFormData.id
        res = await updateDealRecord(formData)
      }
      
      if (res.code === '000000') {
        ElMessage.success(dealRecordFormMode.value === 'add' || dealRecordFormMode.value === 'correct' ? '保存草稿成功' : '更新成功')
        dealRecordFormVisible.value = false
        // 跳转到草稿状态
        queryParams.status = 'draft'
        queryParams.page = 1
        loadDealRecordList()
      } else {
        ElMessage.error(res.message || '保存失败')
      }
    } catch (error) {
      console.error('保存失败:', error)
      ElMessage.error('保存失败')
    } finally {
      dealRecordSaving.value = false
      submitting.value = false
    }
  })
}

// 提交审批
const handleSubmitDealRecord = async () => {
  if (!dealRecordFormRef.value) return
  await dealRecordFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    // 验证成交金额不能大于合同金额
    if (dealRecordFormData.contractAmount && dealRecordFormData.dealAmount > dealRecordFormData.contractAmount) {
      await ElMessageBox.alert('成交金额不能大于合同金额！', '提示', {
        confirmButtonText: '确认',
        type: 'warning'
      })
      return
    }
    
    // 个人用户不需要确认弹窗，直接提交
    if (!isPersonalUser.value) {
      try {
        await ElMessageBox.confirm('确定要提交审批吗？提交后将进入审批流程', '提示', {
          type: 'warning'
        })
      } catch (error) {
        if (error === 'cancel') {
          return
        }
      }
    }
    
    dealRecordSubmitting.value = true
    submitting.value = true
    try {
        // 使用附件上传组件的统一上传逻辑（带进度条）
        if (dealRecordAttachmentUploadRef.value) {
          try {
            const uploadedAttachments = await dealRecordAttachmentUploadRef.value.uploadAll()
            dealRecordFormData.attachments = uploadedAttachments || dealRecordFormData.attachments
          } catch (error) {
            ElMessage.error(`附件上传失败: ${error.message}`)
            throw error
          }
        }
        
        // 处理附件
        const allAttachments = dealRecordFormData.attachments.map(att => typeof att === 'string' ? att : att.url || att).filter(url => url)
        
        const formData = {
          customerId: dealRecordFormData.customerId,
          dealType: dealRecordFormData.dealType,
          dealDate: dealRecordFormData.dealDate,
          dealAmount: dealRecordFormData.dealAmount,
          commissionRate: dealRecordFormData.commissionRate || 0,
          productName: dealRecordFormData.productName || undefined,
          productCategory: dealRecordFormData.productCategory || undefined,
          contractNumber: dealRecordFormData.contractNumber || undefined,
          contractId: dealRecordFormData.contractId || undefined,
          contractAmount: dealRecordFormData.contractAmount || undefined,
          paymentStatus: dealRecordFormData.paymentStatus,
        paymentAmount: dealRecordFormData.paymentAmount || undefined,
          paymentDate: dealRecordFormData.paymentDate || undefined,
          invoiceNumber: dealRecordFormData.invoiceNumber || undefined,
          remark: dealRecordFormData.remark || undefined,
          attachments: allAttachments,
          status: 'pending'
        }
        
        let res
        let dealRecordId
        // 修正模式下也创建新数据
        if (dealRecordFormMode.value === 'add' || dealRecordFormMode.value === 'correct') {
          appendVisitPlanToCreatePayload(formData)
          res = await createDealRecord(formData)
          if (res.code === '000000' && res.data) {
            dealRecordId = res.data.id || res.data.dealRecordId
          }
        } else {
          formData.id = dealRecordFormData.id
          dealRecordId = dealRecordFormData.id
          res = await updateDealRecord(formData)
        }
        
        if (res.code === '000000') {
          // 检查是否需要审批
          let needApproval = true
          if (dealRecordId) {
            try {
              const checkRes = await checkNeedApproval('CUSTOMER_DEAL_RECORD', dealRecordId)
              if (checkRes.code === '000000') {
                needApproval = checkRes.data !== false
              }
            } catch (error) {
              console.warn('检查审批节点失败:', error)
            }
          }
          
          if (needApproval) {
            ElMessage.success('提交审批成功')
          } else {
            ElMessage.success('该流程无需审批，已直接通过')
          }
          dealRecordFormVisible.value = false
          // 个人用户提交审批后，自动切换到已通过状态
          queryParams.status = isPersonalUser.value ? 'approved' : 'pending'
          queryParams.page = 1
          loadDealRecordList()
        } else {
          ElMessage.error(res.message || '提交审批失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('提交审批失败:', error)
          ElMessage.error('提交审批失败')
        }
      } finally {
        dealRecordSubmitting.value = false
        submitting.value = false
      }
  })
}

const handleView = async (row) => {
  dealRecordDetailLoading.value = true
  dealRecordDetailDialogVisible.value = true
  try {
    const res = await getDealRecordDetail(row.id)
    if (res.code === '000000' && res.data) {
      // 清空旧数据
      Object.keys(dealRecordDetail).forEach(key => {
        if (Array.isArray(dealRecordDetail[key])) {
          dealRecordDetail[key] = []
        } else {
          dealRecordDetail[key] = null
        }
      })
      // 处理附件字段（兼容多种字段名：attachment_urls、attachments、attachmentInfos）
      const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      let attachments = []
      
      // 优先使用 attachmentInfos（包含文件大小信息）
      if (res.data.attachmentInfos && Array.isArray(res.data.attachmentInfos) && res.data.attachmentInfos.length > 0) {
        attachments = res.data.attachmentInfos.map(att => {
          let url = att.fileUrl || att.url || att
          if (typeof url === 'string') {
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
          }
          return {
            url,
            fileName: att.fileName || att.originalFileName || url.split('/').pop() || '附件',
            originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
            size: att.size || 0
          }
        })
      } else {
        // 兼容 attachment_urls 和 attachments 字段
        const attachmentUrls = res.data.attachment_urls || res.data.attachments || []
        attachments = Array.isArray(attachmentUrls) ? attachmentUrls : []
        // 异步获取原始文件名和文件大小
        attachments = await Promise.all(attachments.map(async (att) => {
          if (typeof att === 'string') {
            let url = att
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = url.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(url)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(url, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url, 
              fileName: originalFileName, // 使用原始文件名
              originalFileName, // 保存原始文件名
              size: fileSize 
            }
          }
          // 如果已经是对象，直接使用
          return att
        }))
      }
      
      // 填充新数据
      Object.assign(dealRecordDetail, res.data, { attachments })
      // 如果不是草稿状态，加载审批记录
      if (res.data.status !== 'draft') {
        await loadDealRecordApprovalRecords(res.data.id)
      } else {
        dealRecordApprovalRecords.value = []
      }
    } else {
      ElMessage.error(res.message || '获取成交台账详情失败')
      dealRecordDetailDialogVisible.value = false
    }
  } catch (error) {
    console.error('获取成交台账详情失败:', error)
    ElMessage.error('获取成交台账详情失败')
    dealRecordDetailDialogVisible.value = false
  } finally {
    dealRecordDetailLoading.value = false
  }
}

// 查看审批进度（只显示审批记录）
const handleViewApprovalProgress = async (row) => {
  approvalProgressDialogVisible.value = true
  approvalProgressLoading.value = true
  currentDealRecordIdForApproval.value = row.id
  try {
    const res = await getApprovalRecords('CUSTOMER_DEAL_RECORD', row.id)
    if (res.code === '000000') {
      approvalProgressRecords.value = res.data || []
    } else {
      approvalProgressRecords.value = []
      ElMessage.error(res.message || '获取审批记录失败')
    }
  } catch (error) {
    console.error('获取审批记录失败:', error)
    ElMessage.error('获取审批记录失败')
    approvalProgressRecords.value = []
  } finally {
    approvalProgressLoading.value = false
  }
}

// 关闭审批记录对话框
const handleApprovalProgressDialogClose = () => {
  approvalProgressRecords.value = []
  currentDealRecordIdForApproval.value = null
}

// 从列表撤销审批
const handleWithdrawDealRecordFromList = async (row) => {
  try {
    await ElMessageBox.confirm('确定要撤销该成交台账吗？撤销后变为草稿状态', '提示', {
      type: 'warning'
    })
    
    // 调用撤销接口
    const res = await withdrawDealRecord(row.id)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      // 跳转到草稿状态
      queryParams.status = 'draft'
      queryParams.page = 1
      loadDealRecordList()
    } else {
      ElMessage.error(res.message || '撤销失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销失败:', error)
      ElMessage.error(error.message || '撤销失败')
    }
  }
}

// 加载成交台账审批记录
const loadDealRecordApprovalRecords = async (dealRecordId) => {
  dealRecordApprovalRecordLoading.value = true
  try {
    const res = await getApprovalRecords('CUSTOMER_DEAL_RECORD', dealRecordId)
    if (res.code === '000000') {
      dealRecordApprovalRecords.value = res.data || []
    } else {
      dealRecordApprovalRecords.value = []
    }
  } catch (error) {
    console.error('获取审批记录失败:', error)
    dealRecordApprovalRecords.value = []
  } finally {
    dealRecordApprovalRecordLoading.value = false
  }
}

// 从详情编辑
const handleEditFromDetail = () => {
  if (dealRecordDetail.id) {
    // 传入完整的详情对象，包含 status，避免编辑时状态检查失败
    dealRecordDetailDialogVisible.value = false
    handleEdit(dealRecordDetail)
  }
}

// 从详情查看合同
const handleViewContractFromDetail = async () => {
  if (!dealRecordDetail.contractNumber) {
    ElMessage.warning('该成交台账未关联合同')
    return
  }
  
  let contractId = dealRecordDetail.contractId
  if (!contractId && dealRecordDetail.contractNumber) {
    try {
      const contractListRes = await getContractList({
        contractNumber: dealRecordDetail.contractNumber,
        page: 1,
        pageSize: 1
      })
      if (contractListRes.code === '000000' && contractListRes.data?.list?.length > 0) {
        contractId = contractListRes.data.list[0].contractId || contractListRes.data.list[0].id
      } else {
        ElMessage.warning('未找到对应的合同信息')
        return
      }
    } catch (error) {
      console.error('查找合同失败:', error)
      ElMessage.error('查找合同失败，请稍后重试')
      return
    }
  }
  
  if (!contractId) {
    ElMessage.warning('无法获取合同ID，请联系管理员')
    return
  }
  
  dealRecordDetailDialogVisible.value = false
  await handleViewContract({ contractId, contractNumber: dealRecordDetail.contractNumber })
}

// 提交审批（从详情）
const handleSubmitDealRecordApproval = async () => {
  // 个人用户不需要确认弹窗，直接提交
  if (!isPersonalUser.value) {
    try {
      await ElMessageBox.confirm('确定要提交审批吗？提交后将进入审批流程', '提示', {
        type: 'warning'
      })
    } catch (error) {
      if (error === 'cancel') {
        return
      }
    }
  }
  
  try {
    const dealRecordId = dealRecordDetail.id
    if (!dealRecordId) {
      ElMessage.error('成交台账ID不存在')
      return
    }
    
    // 检查是否需要审批
    let needApproval = true
    try {
      const checkRes = await checkNeedApproval('CUSTOMER_DEAL_RECORD', dealRecordId)
      if (checkRes.code === '000000') {
        needApproval = checkRes.data !== false
      }
    } catch (error) {
      console.warn('检查审批节点失败:', error)
    }
    
    // 调用更新接口，设置 status 为 pending
    const res = await updateDealRecord({
      id: dealRecordId,
      customerId: dealRecordDetail.customerId,
      dealType: dealRecordDetail.dealType,
      dealDate: dealRecordDetail.dealDate,
      dealAmount: dealRecordDetail.dealAmount,
      commissionRate: dealRecordDetail.commissionRate || 0,
      productName: dealRecordDetail.productName || undefined,
      productCategory: dealRecordDetail.productCategory || undefined,
      contractNumber: dealRecordDetail.contractNumber,
      contractId: dealRecordDetail.contractId || undefined,
      contractAmount: dealRecordDetail.contractAmount || undefined,
      paymentStatus: dealRecordDetail.paymentStatus,
      paymentDate: dealRecordDetail.paymentDate || undefined,
      invoiceNumber: dealRecordDetail.invoiceNumber || undefined,
      remark: dealRecordDetail.remark || undefined,
      // 详情中再次提交审批时，提取附件 URL 列表传给后端 DTO 的 attachments 字段
      attachments: dealRecordDetail.attachments && dealRecordDetail.attachments.length
        ? dealRecordDetail.attachments.map(att => {
            // 如果是字符串，直接返回
            if (typeof att === 'string') return att
            // 如果是对象，提取URL
            return att.url || ''
          }).filter(url => url)
        : [],
      status: 'pending'
    })
    if (res.code === '000000') {
      if (needApproval) {
        ElMessage.success('提交审批成功')
      } else {
        ElMessage.success('该流程无需审批，已直接通过')
      }
      // 关闭详情对话框
      dealRecordDetailDialogVisible.value = false
      // 个人用户提交审批后，自动切换到已通过状态
      queryParams.status = isPersonalUser.value ? 'approved' : 'pending'
      queryParams.page = 1
      loadDealRecordList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('提交审批失败:', error)
      ElMessage.error('提交审批失败')
    }
  }
}

// 撤销审批
const handleWithdrawDealRecord = async () => {
  try {
    await ElMessageBox.confirm('确定要撤销该成交台账吗？撤销后变为草稿状态', '提示', {
      type: 'warning'
    })
    const dealRecordId = dealRecordDetail.id
    if (!dealRecordId) {
      ElMessage.error('成交台账ID不存在')
      return
    }
    // 调用撤销接口
    const res = await withdrawDealRecord(dealRecordId)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      // 关闭详情对话框
      dealRecordDetailDialogVisible.value = false
      // 跳转到草稿状态
      queryParams.status = 'draft'
      queryParams.page = 1
      loadDealRecordList()
    } else {
      ElMessage.error(res.message || '撤销失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销失败:', error)
      ElMessage.error(error.message || '撤销失败')
    }
  }
}

// 关闭成交台账详情对话框
const handleDealRecordDetailDialogClose = () => {
  // 清空详情数据
  Object.keys(dealRecordDetail).forEach(key => {
    if (Array.isArray(dealRecordDetail[key])) {
      dealRecordDetail[key] = []
    } else {
      dealRecordDetail[key] = null
    }
  })
  // 清空审批记录
  dealRecordApprovalRecords.value = []
}

// 成交台账详情相关
const dealRecordDetailDialogVisible = ref(false)
const dealRecordDetailLoading = ref(false)
const dealRecordApprovalRecordLoading = ref(false)
const dealRecordApprovalRecords = ref([])
const dealRecordDetail = reactive({})

// 审批记录对话框相关
const approvalProgressDialogVisible = ref(false)
const approvalProgressLoading = ref(false)
const approvalProgressRecords = ref([])
const currentDealRecordIdForApproval = ref(null)

// 合同详情相关
const contractDetailDialogVisible = ref(false)
const contractDetailLoading = ref(false)
const contractDetail = reactive({
  contractId: null,
  contractNumber: '',
  customerName: '',
  contractType: '',
  contractStatus: '',
  contractAmount: null,
  startDate: '',
  endDate: '',
  remark: '',
  contractImages: [],
  contractAttachments: [],
  draftBy: '',
  draftTime: null,
  reviewBy: '',
  reviewTime: null,
  createBy: '',
  createTime: null
})

const handleViewContract = async (row) => {
  // 如果没有合同编号，提示用户
  if (!row.contractNumber) {
    ElMessage.warning('该成交台账未关联合同')
    return
  }
  
  // 如果没有合同ID但有合同编号，尝试通过合同编号查找合同ID
  let contractId = row.contractId
  if (!contractId && row.contractNumber) {
    try {
      const contractListRes = await getContractList({
        contractNumber: row.contractNumber,
        page: 1,
        pageSize: 1
      })
      if (contractListRes.code === '000000' && contractListRes.data?.list?.length > 0) {
        contractId = contractListRes.data.list[0].contractId || contractListRes.data.list[0].id
      } else {
        ElMessage.warning('未找到对应的合同信息')
        return
      }
    } catch (error) {
      console.error('查找合同失败:', error)
      ElMessage.error('查找合同失败，请稍后重试')
      return
    }
  }
  
  if (!contractId) {
    ElMessage.warning('无法获取合同ID，请联系管理员')
    return
  }
  
  contractDetailLoading.value = true
  contractDetailDialogVisible.value = true
  try {
    const res = await getContractDetail(contractId)
    if (res.code === '000000') {
      // 清空旧数据
      Object.keys(contractDetail).forEach(key => {
        if (Array.isArray(contractDetail[key])) {
          contractDetail[key] = []
        } else {
          contractDetail[key] = null
        }
      })
      // 填充新数据
      Object.assign(contractDetail, res.data)
      contractDetail.contractId = res.data.contractId
      // 确保数组字段正确初始化
      if (!contractDetail.contractImages) {
        contractDetail.contractImages = []
      }
      if (!contractDetail.contractAttachments) {
        contractDetail.contractAttachments = []
      }

      // 处理附件数据：补全文件名、大小，保证展示一致
      if (contractDetail.contractAttachments && contractDetail.contractAttachments.length > 0) {
        const { getFileName } = await import('../api/file')
        const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
        contractDetail.contractAttachments = await Promise.all(contractDetail.contractAttachments.map(async (att) => {
          // 字符串：仅有URL
          if (typeof att === 'string') {
            let url = att
            if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
            let fileName = url.split('/').pop() || '附件'
            let size = 0
            try {
              const fileNameRes = await getFileName(url)
              if (fileNameRes.code === '000000') {
                fileName = fileNameRes.data || fileNameRes.msg || fileName
              }
            } catch (error) {
              console.warn('获取文件名失败:', error)
            }
            return {
              url,
              fileName,
              originalFileName: fileName,
              size
            }
          }

          // 对象：可能只有 url / fileName
          let url = att.url
          if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          // 已经有 originalFileName 直接返回
          if (att.originalFileName) {
            return { ...att, url }
          }
          const fileName = att.fileName || att.name || (url ? url.split('/').pop() : '附件') || '附件'
          return {
            ...att,
            url,
            fileName,
            originalFileName: fileName,
            size: att.size || 0
          }
        }))
      }
    } else {
      ElMessage.error(res.message || '获取合同详情失败')
      contractDetailDialogVisible.value = false
    }
  } catch (error) {
    console.error('获取合同详情失败:', error)
    ElMessage.error('获取合同详情失败')
    contractDetailDialogVisible.value = false
  } finally {
    contractDetailLoading.value = false
  }
}

const handleContractDetailDialogClose = () => {
  // 清空详情数据
  Object.keys(contractDetail).forEach(key => {
    if (Array.isArray(contractDetail[key])) {
      contractDetail[key] = []
    } else {
      contractDetail[key] = null
    }
  })
}

const getContractTypeName = (type) => {
  const map = {
    intention: '意向合同',
    deal: '成交合同',
    formal: '正式合同',
    framework: '框架合同',
    service: '服务合同',
    other: '其他'
  }
  return map[type] || type || '-'
}

const getContractStatusName = (status) => {
  const map = {
    draft: '草稿',
    pending: '待审批',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[status] || status || '-'
}

const getContractStatusType = (status) => {
  const map = {
    draft: 'info',
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
    cancelled: 'info'
  }
  return map[status] || 'info'
}


const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadDealRecordList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadDealRecordList()
}

// 导出成交台账列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      customerId: queryParams.customerId,
      customerName: queryParams.customerName,
      status: queryParams.status,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    }
    
    const blob = await exportDealRecordList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `成交台账列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

onMounted(() => {
  loadDealCrmMeta()
  loadDealRecordList()
})
</script>

<style scoped>
.deal-record-management {
  padding: 20px;
}

.visit-plan-alert {
  margin-bottom: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.action-buttons-inline {
  display: flex;
  gap: 10px;
}

/* 表格容器，确保自适应 */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
}

/* 表格自适应优化 */
.table-wrapper :deep(.el-table) {
  width: 100%;
  min-width: 100%;
}

/* 小屏幕下优化表格显示 */
@media (max-width: 1400px) {
  .table-wrapper :deep(.el-table) {
    font-size: 13px;
  }
  
  .table-wrapper :deep(.el-table .el-button) {
    padding: 5px 10px;
    font-size: 12px;
  }
}

@media (max-width: 1200px) {
  .table-wrapper :deep(.el-table) {
    font-size: 12px;
  }
  
  .table-wrapper :deep(.el-table .el-button) {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.contract-link {
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.3s;
}

.contract-link:hover {
  color: #66b1ff;
  text-decoration: underline;
}

/* 合同详情对话框样式 */
.contract-detail-container {
  display: flex;
  gap: 20px;
  padding: 0;
  align-items: flex-start;
}

.contract-detail-left {
  flex: 1;
  min-width: 0;
  max-width: 900px;
}

/* 草稿状态下，左侧区域占满整个宽度 */
.contract-detail-left.full-width {
  max-width: 100%;
}

.contract-detail {
  padding: 0;
}

.contract-detail-right {
  width: 420px;
  flex-shrink: 0;
}

.approval-record-card {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

.approval-record-card :deep(.el-card__body) {
  padding: 20px;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}

.detail-card {
  margin-bottom: 20px;
}

.detail-card:last-child {
  margin-bottom: 0;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.title-tip {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.attachment-item:hover {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.attachment-icon {
  color: #409eff;
  font-size: 18px;
}

.attachment-name {
  color: #409eff;
  cursor: pointer;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-name:hover {
  text-decoration: underline;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.footer-left {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
}

.customer-selector {
  position: relative;
}

.clear-icon {
  cursor: pointer;
  color: #909399;
  margin-right: 8px;
}

.clear-icon:hover {
  color: #f56c6c;
}

.arrow-icon {
  color: #909399;
}

.contract-selector {
  display: flex;
  align-items: center;
}

.contract-amount-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #409eff;
}

.form-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.customer-picker-dialog .customer-picker-content {
  padding: 0;
}

.customer-picker-search {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.customer-picker-list {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.customer-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.customer-picker-item:hover {
  background-color: #f5f7fa;
  border-color: #409eff;
}

.customer-picker-item.active {
  background-color: #ecf5ff;
  border-color: #409eff;
}

.customer-item-main {
  flex: 1;
}

.customer-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.customer-meta {
  font-size: 12px;
  color: #909399;
}

.customer-meta .phone {
  margin-left: 8px;
}

.no-contact {
  color: #c0c4cc;
}

.check-icon {
  color: #409eff;
  font-size: 18px;
}

.customer-picker-pagination {
  margin-top: 16px;
}

/* 附件列表样式（参考报销申请） */
.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
}

.attachment-list .attachment-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s;
  max-width: 520px;
}

.attachment-list .attachment-item:hover {
  background-color: #f0f2f5;
  border-color: #c0c4cc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 图片附件样式 */
.attachment-list .attachment-item-image {
  display: flex;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid #e4e7ed;
  background-color: #ffffff;
}

.attachment-list .attachment-item-image:hover {
  border-color: #c0c4cc;
}

.attachment-image-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin-right: 14px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.attachment-image-wrapper:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.attachment-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.attachment-image-preview:hover {
  transform: scale(1.05);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}

.download-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 1;
  z-index: 3;
}

.download-overlay-btn {
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.attachment-image-info {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 14px;
}

.attachment-image-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.attachment-image-details .attachment-name {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0;
}

.attachment-image-details .attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 0;
}

.attachment-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: #f5f7fa;
  position: relative;
}

.attachment-icon {
  font-size: 24px;
}

.file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.attachment-list .attachment-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
}

.attachment-list .attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 12px;
  flex-shrink: 0;
  min-width: 50px;
}

.attachment-list .attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  justify-content: center;
  margin-top: 4px;
}

/* 文件类型图标样式 */
.icon-excel {
  background: linear-gradient(135deg, #1d6f42 0%, #2a8f56 100%);
}

.icon-excel .attachment-icon {
  color: #fff;
}

.icon-word {
  background: linear-gradient(135deg, #2b579a 0%, #3d6fb8 100%);
}

.icon-word .attachment-icon {
  color: #fff;
}

.icon-pdf {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-pdf .attachment-icon {
  color: #fff;
}

.icon-txt {
  background: linear-gradient(135deg, #666666 0%, #808080 100%);
}

.icon-txt .attachment-icon {
  color: #fff;
}

.icon-image {
  background: linear-gradient(135deg, #4a90e2 0%, #5ba3f5 100%);
}

.icon-image .attachment-icon {
  color: #fff;
}

.icon-ppt {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-ppt .attachment-icon {
  color: #fff;
}

.icon-zip {
  background: linear-gradient(135deg, #ff9800 0%, #ffb340 100%);
}

.icon-zip .attachment-icon {
  color: #fff;
}

.icon-default {
  background-color: #909399;
}

.icon-default .attachment-icon {
  color: #fff;
}

.attachment-upload-container {
  width: 100%;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}

.attachment-item {
  position: relative;
  display: inline-block;
}

.attachment-upload-container .attachment-item .attachment-actions {
  position: absolute;
  top: -8px;
  right: -8px;
}

.attachment-file {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f5f7fa;
}

.attachment-file .el-icon {
  margin-right: 8px;
  color: #409eff;
}

.attachment-name {
  font-size: 12px;
  color: #606266;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-uploader {
  display: inline-block;
}

.add-image-btn {
  width: 80px;
  height: 80px;
  border: 1px dashed #d9d9d9;
  background-color: #fafafa;
  color: #8c8c8c;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  color: #409eff;
  background-color: #f0f9ff;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.upload-tip .tip-item {
  margin: 2px 0;
  margin-top: 8px;
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>

